import React, { useState } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Search,
  Filter,
  Download,
  Mail,
  Phone,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Calendar,
  Building2,
  MapPin
} from 'lucide-react';

// Mock de afiliados con datos completos
const mockAffilates = [
  { 
    id: '1', 
    name: 'María García López', 
    email: 'maria.garcia@email.com', 
    phone: '55 1234 5678',
    status: 'active', 
    plan: 'Titular',
    company: 'Inmobiliaria García',
    location: 'Ciudad de México',
    startDate: '2024-01-15',
    endDate: '2025-01-15',
    daysRemaining: 180,
    totalPayments: 3500,
    certifications: ['EC0110.02']
  },
  { 
    id: '2', 
    name: 'Carlos Rodríguez Pérez', 
    email: 'carlos.rodriguez@email.com', 
    phone: '55 9876 5432',
    status: 'expiring', 
    plan: 'Titular',
    company: 'Bienes Raíces CR',
    location: 'Guadalajara',
    startDate: '2024-07-20',
    endDate: '2025-07-20',
    daysRemaining: 15,
    totalPayments: 3500,
    certifications: []
  },
  { 
    id: '3', 
    name: 'Ana Martínez Silva', 
    email: 'ana.martinez@email.com', 
    phone: '55 5555 1234',
    status: 'active', 
    plan: 'Sub-afiliación (1-5)',
    company: 'Grupo Inmobiliario AM',
    location: 'Monterrey',
    startDate: '2024-03-10',
    endDate: '2025-03-10',
    daysRemaining: 240,
    totalPayments: 1000,
    certifications: ['EC0110.02']
  },
  { 
    id: '4', 
    name: 'Pedro Sánchez Morales', 
    email: 'pedro.sanchez@email.com', 
    phone: '55 4444 5555',
    status: 'expired', 
    plan: 'Titular',
    company: 'Sánchez Propiedades',
    location: 'Puebla',
    startDate: '2023-07-01',
    endDate: '2024-07-01',
    daysRemaining: -30,
    totalPayments: 3500,
    certifications: []
  },
  { 
    id: '5', 
    name: 'Laura Hernández Díaz', 
    email: 'laura.hernandez@email.com', 
    phone: '55 6666 7777',
    status: 'active', 
    plan: 'Titular',
    company: 'Inmuebles LH',
    location: 'Querétaro',
    startDate: '2024-05-20',
    endDate: '2025-05-20',
    daysRemaining: 300,
    totalPayments: 6979,
    certifications: ['EC0110.02']
  },
  { 
    id: '6', 
    name: 'Roberto Flores Luna', 
    email: 'roberto.flores@email.com', 
    phone: '55 8888 9999',
    status: 'grace', 
    plan: 'Titular',
    company: 'Flores Real Estate',
    location: 'León',
    startDate: '2024-06-30',
    endDate: '2025-06-30',
    daysRemaining: -5,
    totalPayments: 3500,
    certifications: []
  },
];

const AdminAfiliados = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Activo</Badge>;
      case 'expiring':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Por vencer</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Vencido</Badge>;
      case 'grace':
        return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Periodo de gracia</Badge>;
      default:
        return <Badge variant="outline">Desconocido</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'expiring':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case 'expired':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'grace':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return null;
    }
  };

  const filteredAffiliates = mockAffilates.filter(affiliate => {
    const matchesSearch = 
      affiliate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affiliate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affiliate.company.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || affiliate.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    all: mockAffilates.length,
    active: mockAffilates.filter(a => a.status === 'active').length,
    expiring: mockAffilates.filter(a => a.status === 'expiring').length,
    expired: mockAffilates.filter(a => a.status === 'expired').length,
    grace: mockAffilates.filter(a => a.status === 'grace').length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-afiliados-title">
              Afiliados
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Gestiona los afiliados de UPIM
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <Button variant="outline" className="border-gray-300">
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
            <Button className="bg-gray-900 hover:bg-gray-800">
              Agregar Afiliado
            </Button>
          </div>
        </div>

        {/* Status Filters */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'Todos' },
            { value: 'active', label: 'Activos' },
            { value: 'expiring', label: 'Por vencer' },
            { value: 'grace', label: 'Periodo de gracia' },
            { value: 'expired', label: 'Vencidos' },
          ].map((filter) => (
            <Button
              key={filter.value}
              variant={statusFilter === filter.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(filter.value)}
              className={statusFilter === filter.value ? 'bg-gray-900 hover:bg-gray-800' : 'border-gray-300'}
              data-testid={`filter-${filter.value}`}
            >
              {filter.label}
              <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                {statusCounts[filter.value]}
              </span>
            </Button>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar por nombre, email o empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-affiliates"
          />
        </div>

        {/* Affiliates Table */}
        <Card className="border border-gray-100">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Afiliado</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Plan</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Estado</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Vigencia</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Ubicación</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAffiliates.map((affiliate) => (
                    <tr 
                      key={affiliate.id} 
                      className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
                      data-testid={`affiliate-row-${affiliate.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-medium text-gray-600">
                              {affiliate.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{affiliate.name}</p>
                            <p className="text-sm text-gray-500">{affiliate.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-900">{affiliate.plan}</span>
                      </td>
                      <td className="p-4">
                        {getStatusBadge(affiliate.status)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(affiliate.status)}
                          <div>
                            <p className="text-sm text-gray-900">{affiliate.endDate}</p>
                            <p className={`text-xs ${affiliate.daysRemaining > 30 ? 'text-green-600' : affiliate.daysRemaining > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                              {affiliate.daysRemaining > 0 
                                ? `${affiliate.daysRemaining} días restantes`
                                : `Vencido hace ${Math.abs(affiliate.daysRemaining)} días`
                              }
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-1 text-sm text-gray-600">
                          <MapPin className="h-4 w-4" />
                          <span>{affiliate.location}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-8 w-8 p-0"
                            onClick={() => setSelectedAffiliate(affiliate)}
                          >
                            <Mail className="h-4 w-4 text-gray-500" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-8 w-8 p-0"
                          >
                            <Phone className="h-4 w-4 text-gray-500" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-8 w-8 p-0"
                          >
                            <MoreVertical className="h-4 w-4 text-gray-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Mostrando {filteredAffiliates.length} de {mockAffilates.length} afiliados
              </p>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" className="border-gray-300" disabled>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" className="border-gray-300 bg-gray-900 text-white">
                  1
                </Button>
                <Button variant="outline" size="sm" className="border-gray-300">
                  2
                </Button>
                <Button variant="outline" size="sm" className="border-gray-300">
                  3
                </Button>
                <Button variant="outline" size="sm" className="border-gray-300">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Affiliate Detail Modal - Simplified inline */}
        {selectedAffiliate && (
          <Card className="border border-gray-200 mt-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">Detalle del Afiliado</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedAffiliate(null)}>
                Cerrar
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Información Personal</h4>
                  <div className="space-y-2">
                    <p className="text-sm"><span className="text-gray-500">Nombre:</span> {selectedAffiliate.name}</p>
                    <p className="text-sm"><span className="text-gray-500">Email:</span> {selectedAffiliate.email}</p>
                    <p className="text-sm"><span className="text-gray-500">Teléfono:</span> {selectedAffiliate.phone}</p>
                    <p className="text-sm"><span className="text-gray-500">Empresa:</span> {selectedAffiliate.company}</p>
                    <p className="text-sm"><span className="text-gray-500">Ubicación:</span> {selectedAffiliate.location}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Información de Membresía</h4>
                  <div className="space-y-2">
                    <p className="text-sm"><span className="text-gray-500">Plan:</span> {selectedAffiliate.plan}</p>
                    <p className="text-sm"><span className="text-gray-500">Estado:</span> {getStatusBadge(selectedAffiliate.status)}</p>
                    <p className="text-sm"><span className="text-gray-500">Inicio:</span> {selectedAffiliate.startDate}</p>
                    <p className="text-sm"><span className="text-gray-500">Vencimiento:</span> {selectedAffiliate.endDate}</p>
                    <p className="text-sm"><span className="text-gray-500">Total pagado:</span> ${selectedAffiliate.totalPayments.toLocaleString()}</p>
                    <p className="text-sm">
                      <span className="text-gray-500">Certificaciones:</span> {' '}
                      {selectedAffiliate.certifications.length > 0 
                        ? selectedAffiliate.certifications.join(', ')
                        : 'Ninguna'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button className="bg-gray-900 hover:bg-gray-800">
                  <Mail className="h-4 w-4 mr-2" />
                  Enviar mensaje
                </Button>
                <Button variant="outline" className="border-gray-300">
                  Editar información
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAfiliados;
